/**
 * Secure Offline Storage Service
 *
 * Provides encrypted offline storage using localForage (IndexedDB)
 * with AES-GCM encryption via Web Crypto API.
 *
 * Features:
 * - Async storage (non-blocking)
 * - AES-256-GCM encryption for sensitive data
 * - Automatic sync queue management
 * - Online/offline event handling
 *
 * =============================================================================
 * CODE REVIEW TODOs (2026-01-14) - See claudedocs/CODE_REVIEW_2026_01_14.md
 * =============================================================================
 *
 * DONE [CRITICAL]: OFFLINE QUEUE - queueOfflineRequest() is now wired up to
 *   UnifiedInspectionWorkflow handleSubmit(). When network errors are detected,
 *   inspection data is queued for later sync. Reference: Priority 1.3
 *   Completed: 2026-01-15
 *
 * DONE [HIGH]: SERVICE WORKER SYNC - Added backgroundSync.js service with
 *   service worker registration, exponential backoff with jitter, and
 *   automatic sync when coming online. Completed: 2026-02-08
 *
 * DONE [MEDIUM]: PENDING SYNC INDICATOR - Added SyncStatusIndicator component
 *   showing queued count badge, status popover, and manual sync button.
 *   Integrated into UnifiedInspectionWorkflow. Completed: 2026-02-08
 */
import localforage from 'localforage';

// Configure localForage instance
localforage.config({
  name: 'ScaleInspection',
  storeName: 'offlineData',
  description: 'Encrypted offline storage for inspection data'
});

// ============ Encryption Layer (Web Crypto API - no extra deps) ============

let encryptionKey = null;

/**
 * Get or create encryption key
 * Key is stored in localForage and persists across sessions
 */
async function getEncryptionKey() {
  if (encryptionKey) return encryptionKey;

  try {
    // Try to load existing key
    const storedKey = await localforage.getItem('_encKey');
    if (storedKey) {
      encryptionKey = await crypto.subtle.importKey(
        'raw',
        new Uint8Array(storedKey),
        { name: 'AES-GCM' },
        false,
        ['encrypt', 'decrypt']
      );
      return encryptionKey;
    }

    // Generate new key if none exists
    encryptionKey = await crypto.subtle.generateKey(
      { name: 'AES-GCM', length: 256 },
      true,
      ['encrypt', 'decrypt']
    );

    // Export and store the key
    const exportedKey = await crypto.subtle.exportKey('raw', encryptionKey);
    await localforage.setItem('_encKey', Array.from(new Uint8Array(exportedKey)));

    return encryptionKey;
  } catch (error) {
    console.error('Encryption key error:', error);
    throw new Error('Failed to initialize encryption');
  }
}

/**
 * Encrypt data using AES-GCM
 */
async function encrypt(data) {
  const key = await getEncryptionKey();
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const encoded = new TextEncoder().encode(JSON.stringify(data));

  const encrypted = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    encoded
  );

  return {
    iv: Array.from(iv),
    data: Array.from(new Uint8Array(encrypted))
  };
}

/**
 * Decrypt data using AES-GCM
 */
async function decrypt(encrypted) {
  if (!encrypted || !encrypted.iv || !encrypted.data) {
    return null;
  }

  const key = await getEncryptionKey();

  const decrypted = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: new Uint8Array(encrypted.iv) },
    key,
    new Uint8Array(encrypted.data)
  );

  return JSON.parse(new TextDecoder().decode(decrypted));
}

// ============ Offline Queue Management ============

const QUEUE_KEY = 'offlineQueue';

/**
 * Generate a unique idempotency key for duplicate prevention
 * Format: {table}_{jobId}_{scaleId}_{timestamp}_{random}
 * Server-side should reject requests with duplicate idempotency keys
 */
export function generateIdempotencyKey(request) {
  const parts = [
    request.table || 'unknown',
    request.data?.job_id || 'nojob',
    request.data?.scale_id || 'noscale',
    Date.now(),
    Math.random().toString(36).substr(2, 9)
  ];
  return parts.join('_');
}

/**
 * Queue a request for later sync
 * @param {Object} request - Request to queue
 * @param {string} request.table - Supabase table name
 * @param {string} request.method - Method: 'insert', 'update', 'upsert', 'delete'
 * @param {Object} request.data - Data payload
 * @param {string} [request.match] - Match criteria for update/delete
 * @param {string} [request.idempotencyKey] - Optional pre-generated key for duplicate prevention
 */
export async function queueOfflineRequest(request) {
  try {
    const queue = await getOfflineQueue();
    
    // Phase 2 FIX (2.2-A): Add idempotency key for duplicate prevention
    // This key is sent with the request and server rejects duplicates
    const idempotencyKey = request.idempotencyKey || generateIdempotencyKey(request);
    
    // Check if this idempotency key already exists in queue (prevent client-side duplicates)
    const existingRequest = queue.find(item => 
      item.idempotencyKey === idempotencyKey && 
      item.status !== 'completed'
    );
    
    if (existingRequest) {
      console.log('Request with same idempotency key already queued:', idempotencyKey);
      return existingRequest;
    }
    
    const queuedRequest = {
      ...request,
      id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      idempotencyKey,
      timestamp: new Date().toISOString(),
      status: 'pending',
      retryCount: 0
    };

    queue.push(queuedRequest);
    await localforage.setItem(QUEUE_KEY, await encrypt(queue));

    console.log('Request queued for offline sync:', queuedRequest.id, 'idempotencyKey:', idempotencyKey);
    return queuedRequest;
  } catch (error) {
    console.error('Failed to queue offline request:', error);
    throw error;
  }
}

/**
 * Get all queued offline requests
 */
export async function getOfflineQueue() {
  try {
    const stored = await localforage.getItem(QUEUE_KEY);
    if (!stored) return [];
    return await decrypt(stored) || [];
  } catch (error) {
    console.error('Failed to get offline queue:', error);
    return [];
  }
}

/**
 * Get count of pending items in queue
 */
export async function getQueueCount() {
  const queue = await getOfflineQueue();
  return queue.filter(item => item.status === 'pending').length;
}

/**
 * Remove a specific item from the queue
 */
export async function removeFromQueue(id) {
  try {
    const queue = await getOfflineQueue();
    const filtered = queue.filter(item => item.id !== id);
    await localforage.setItem(QUEUE_KEY, await encrypt(filtered));
  } catch (error) {
    console.error('Failed to remove from queue:', error);
    throw error;
  }
}

/**
 * Update status of a queued item
 * @param {string} id - Queue item ID
 * @param {string} status - New status: 'pending', 'completed', 'failed'
 * @param {string|Object} metadata - Error string or metadata object
 *   - If string: treated as error message
 *   - If object: merged into item (supports retryCount, error, nextRetryAt, etc.)
 */
export async function updateQueueItemStatus(id, status, metadata = null) {
  try {
    const queue = await getOfflineQueue();
    const updated = queue.map(item => {
      if (item.id === id) {
        // Build update object
        const updates = {
          ...item,
          status,
          processedAt: status === 'completed' ? new Date().toISOString() : item.processedAt
        };
        
        // Handle metadata - can be string (error) or object
        if (typeof metadata === 'string') {
          updates.lastError = metadata;
          if (status === 'failed') {
            updates.retryCount = (item.retryCount || 0) + 1;
          }
        } else if (metadata && typeof metadata === 'object') {
          // Merge metadata object
          Object.assign(updates, metadata);
          // Preserve lastError from metadata.error
          if (metadata.error) {
            updates.lastError = metadata.error;
          }
        } else if (status === 'failed') {
          updates.retryCount = (item.retryCount || 0) + 1;
        }
        
        return updates;
      }
      return item;
    });
    await localforage.setItem(QUEUE_KEY, await encrypt(updated));
  } catch (error) {
    console.error('Failed to update queue item:', error);
    throw error;
  }
}

/**
 * Clear all completed items from the queue
 */
export async function clearCompletedFromQueue() {
  try {
    const queue = await getOfflineQueue();
    const pending = queue.filter(item => item.status !== 'completed');
    await localforage.setItem(QUEUE_KEY, await encrypt(pending));
    return queue.length - pending.length; // Return number of items cleared
  } catch (error) {
    console.error('Failed to clear completed items:', error);
    throw error;
  }
}

/**
 * Clear the entire queue
 */
export async function clearQueue() {
  try {
    await localforage.removeItem(QUEUE_KEY);
  } catch (error) {
    console.error('Failed to clear queue:', error);
    throw error;
  }
}

// ============ Phase 2 FIX (2.2-B): Offline Queue Processing ============

let isProcessingQueue = false;
let externalProcessingFlag = false; // Set by backgroundSync to prevent race conditions
let processQueueCallbacks = [];

/**
 * Set external processing flag (called by backgroundSync to prevent race conditions)
 * @param {boolean} processing - Whether external processing is active
 */
export function setExternalQueueProcessing(processing) {
  externalProcessingFlag = processing;
}

/**
 * Register a callback to be called when queue processing completes
 * @param {Function} callback - Called with { processed, failed, remaining }
 */
export function onQueueProcessed(callback) {
  processQueueCallbacks.push(callback);
  return () => {
    processQueueCallbacks = processQueueCallbacks.filter(cb => cb !== callback);
  };
}

/**
 * Process all pending items in the offline queue
 * Called automatically when device comes back online
 * 
 * @param {Function} submitFn - Function to submit each item (e.g., supabase.functions.invoke)
 * @returns {Object} { processed: number, failed: number, remaining: number }
 */
export async function processOfflineQueue(submitFn) {
  // Check BOTH local and external mutex to prevent race conditions
  // External flag is set by backgroundSync when it's processing
  if (isProcessingQueue || externalProcessingFlag) {
    console.log('[OfflineQueue] Already processing (internal or external), skipping');
    return { processed: 0, failed: 0, remaining: await getQueueCount() };
  }

  isProcessingQueue = true;
  let processed = 0;
  let failed = 0;

  try {
    const queue = await getOfflineQueue();
    const pending = queue.filter(item => item.status === 'pending' || item.status === 'failed');

    console.log(`[OfflineQueue] Processing ${pending.length} pending items`);

    for (const item of pending) {
      // Skip if too many retries
      if (item.retryCount >= 3) {
        console.log(`[OfflineQueue] Item ${item.id} exceeded max retries, marking as failed`);
        await updateQueueItemStatus(item.id, 'failed', 'Max retries exceeded');
        failed++;
        continue;
      }

      try {
        // Exponential backoff: 1s, 2s, 4s
        if (item.retryCount > 0) {
          const backoffMs = Math.pow(2, item.retryCount) * 1000;
          console.log(`[OfflineQueue] Waiting ${backoffMs}ms before retry`);
          await new Promise(resolve => setTimeout(resolve, backoffMs));
        }

        // Submit the item
        const result = await submitFn(item);

        if (result.success || result.data?.success) {
          await updateQueueItemStatus(item.id, 'completed');
          processed++;
          console.log(`[OfflineQueue] Item ${item.id} processed successfully`);
        } else {
          throw new Error(result.error || result.data?.error || 'Unknown error');
        }
      } catch (error) {
        console.error(`[OfflineQueue] Failed to process item ${item.id}:`, error);
        await updateQueueItemStatus(item.id, 'failed', error.message);
        failed++;
      }
    }

    // Clear completed items
    await clearCompletedFromQueue();

    const remaining = await getQueueCount();
    const result = { processed, failed, remaining };

    // Notify callbacks
    processQueueCallbacks.forEach(cb => cb(result));

    console.log(`[OfflineQueue] Complete: ${processed} processed, ${failed} failed, ${remaining} remaining`);
    return result;
  } finally {
    isProcessingQueue = false;
  }
}

/**
 * Check if queue is currently being processed
 */
export function isQueueProcessing() {
  return isProcessingQueue;
}

// ============ Generic Secure Storage ============

/**
 * Store encrypted data
 */
export async function setSecureItem(key, value) {
  try {
    const encrypted = await encrypt(value);
    await localforage.setItem(key, encrypted);
  } catch (error) {
    console.error(`Failed to store ${key}:`, error);
    throw error;
  }
}

/**
 * Retrieve and decrypt data
 */
export async function getSecureItem(key) {
  try {
    const stored = await localforage.getItem(key);
    if (!stored) return null;
    return await decrypt(stored);
  } catch (error) {
    console.error(`Failed to retrieve ${key}:`, error);
    return null;
  }
}

/**
 * Remove an item
 */
export async function removeSecureItem(key) {
  try {
    await localforage.removeItem(key);
  } catch (error) {
    console.error(`Failed to remove ${key}:`, error);
    throw error;
  }
}

// ============ Storage Info ============

/**
 * Get storage usage information
 */
export async function getStorageInfo() {
  try {
    if (navigator.storage && navigator.storage.estimate) {
      const estimate = await navigator.storage.estimate();
      return {
        used: estimate.usage,
        quota: estimate.quota,
        usedMB: (estimate.usage / 1024 / 1024).toFixed(2),
        quotaMB: (estimate.quota / 1024 / 1024).toFixed(2),
        percentUsed: ((estimate.usage / estimate.quota) * 100).toFixed(1)
      };
    }
    return null;
  } catch (error) {
    console.error('Failed to get storage info:', error);
    return null;
  }
}

/**
 * Request persistent storage (prevents browser from clearing data)
 */
export async function requestPersistentStorage() {
  try {
    if (navigator.storage && navigator.storage.persist) {
      const granted = await navigator.storage.persist();
      console.log(granted ? 'Persistent storage granted' : 'Persistent storage denied');
      return granted;
    }
    return false;
  } catch (error) {
    console.error('Failed to request persistent storage:', error);
    return false;
  }
}

// ============ Export default instance ============

export default {
  // Queue management
  queueOfflineRequest,
  generateIdempotencyKey,
  getOfflineQueue,
  getQueueCount,
  removeFromQueue,
  updateQueueItemStatus,
  clearCompletedFromQueue,
  clearQueue,
  // Phase 2: Queue processing
  processOfflineQueue,
  onQueueProcessed,
  isQueueProcessing,
  setExternalQueueProcessing,
  // Secure storage
  setSecureItem,
  getSecureItem,
  removeSecureItem,
  // Storage info
  getStorageInfo,
  requestPersistentStorage
};
